import { Checkbox, Icon, mergeStyles, Stack, TooltipHost } from '@fluentui/react';
import * as React from 'react';

import { SubscriptionNode, SubscriptionNodeTypes } from '../../../../Common/ConsumerModels/SubcriptionNode';
import * as Styled from '../../../../Common/Styles/attributeSubscriptions.styled';
import { AttributeSubscriptions } from './AttributeSubscriptions';
import { EgressOptionType } from '../../../../Common/CommonModels/EgressOptionTypeEnum';

type AddAttributeSubscriptionsFormProps = {
  formattedSubscriptionNodes: SubscriptionNode[];
  setFormattedSubscriptionNodes(subscriptionNodes: SubscriptionNode[]): void;
  filteredSubscriptionNodes: SubscriptionNode[];
  setFilteredSubscriptionNodes(subscriptionNodes: SubscriptionNode[]): void;
  isAttributeSubscriptionLoading: boolean;
  searchQuery: string;
  checkedAttributeCount: number;
  setCheckedAttributeCount(val: number): void;
  filteredTenantIndex: number;
  egressType: EgressOptionType;
};

export function CdmSubscriptions(props: AddAttributeSubscriptionsFormProps): React.ReactElement {
  const {
    formattedSubscriptionNodes,
    setFormattedSubscriptionNodes,
    filteredSubscriptionNodes,
    setFilteredSubscriptionNodes,
    isAttributeSubscriptionLoading,
    searchQuery,
    checkedAttributeCount,
    setCheckedAttributeCount,
    filteredTenantIndex,
    egressType
  } = props;
  
  const checkBoxStyles = {
    text: {
      padding: '0px 0px 0px 4px',
    },
  };

  const lockIconClass = mergeStyles({
    fontSize: 14,
    height: 14,
    width: 14,
    color: '#605E5C',
    marginLeft: 8,
  });

  const getFormattedIndexesFromAttributeNode = (pathFromRoot: string) => {
    const formattedIndexesList: number[] = pathFromRoot.split('###') as unknown as number[];
    const formattedTenantIndex: number = formattedIndexesList[0];
    const formattedCdmIndex: number = formattedIndexesList[1];
    const formattedAttributeIndex: number = formattedIndexesList[2];
    return { formattedTenantIndex, formattedCdmIndex, formattedAttributeIndex };
  };

  const getFormattedIndexesFromCdmNode = (pathFromRoot: string) => {
    const formattedIndexesList: number[] = pathFromRoot.split('###') as unknown as number[];
    const formattedTenantIndex: number = formattedIndexesList[0];
    const formattedCdmIndex: number = formattedIndexesList[1];
    return { formattedTenantIndex, formattedCdmIndex };
  };

  const onChangeSelectAll = (filteredTenantIndex: number, filteredCdmIndex: number): void => {

    const newFilteredSubscriptionNodes: SubscriptionNode[] = JSON.parse(
      JSON.stringify(filteredSubscriptionNodes) // deep copy
    );
    const newFormattedSubscriptionNodes: SubscriptionNode[] = JSON.parse(
      JSON.stringify(formattedSubscriptionNodes) // deep copy
    );

    const newFilteredCdm = newFilteredSubscriptionNodes[filteredTenantIndex].children[filteredCdmIndex];

    let numChecked = 0;

    newFilteredCdm.children.forEach((newFilteredAttribute) => {
      if (newFilteredAttribute.isApprovedAttribute) return;
      const { formattedTenantIndex, formattedCdmIndex, formattedAttributeIndex } = 
        getFormattedIndexesFromAttributeNode(newFilteredAttribute.pathFromRoot);
      const newFormattedAttribute = newFormattedSubscriptionNodes[formattedTenantIndex]
        .children[formattedCdmIndex]
        .children[formattedAttributeIndex];
      if (!newFilteredAttribute.isCheckedAttribute) {
        numChecked = numChecked + 1;
      }
      newFilteredAttribute.isCheckedAttribute = true; // filtered tree
      newFormattedAttribute.isCheckedAttribute = true; // original tree
    });

    newFilteredCdm.isAllSelected = true;
    setFilteredSubscriptionNodes(newFilteredSubscriptionNodes);
    setFormattedSubscriptionNodes(newFormattedSubscriptionNodes);

    // Add the attribute count for error validation
    setCheckedAttributeCount(checkedAttributeCount + numChecked);
  };

  const onChangeDeselectAll = (filteredTenantIndex: number, filteredCdmIndex: number): void => {

    const newFilteredSubscriptionNodes: SubscriptionNode[] = JSON.parse(
      JSON.stringify(filteredSubscriptionNodes) // deep copy
    );
    const newFormattedSubscriptionNodes: SubscriptionNode[] = JSON.parse(
      JSON.stringify(formattedSubscriptionNodes) // deep copy
    );
    
    const newFilteredCdm = newFilteredSubscriptionNodes[filteredTenantIndex].children[filteredCdmIndex];

    let numChecked = 0;

    newFilteredCdm.children.forEach((newFilteredAttribute) => {
      if (newFilteredAttribute.isApprovedAttribute) return;
      const { formattedTenantIndex, formattedCdmIndex, formattedAttributeIndex } = 
        getFormattedIndexesFromAttributeNode(newFilteredAttribute.pathFromRoot);
      const newFormattedAttribute = newFormattedSubscriptionNodes[formattedTenantIndex]
        .children[formattedCdmIndex]
        .children[formattedAttributeIndex];
      if (newFilteredAttribute.isCheckedAttribute) {
        numChecked = numChecked + 1;
      }
      newFilteredAttribute.isCheckedAttribute = false; // filtered tree
      newFormattedAttribute.isCheckedAttribute = false; // formatted tree
    });

    newFilteredCdm.isAllSelected = false;
    setFilteredSubscriptionNodes(newFilteredSubscriptionNodes);
    setFormattedSubscriptionNodes(newFormattedSubscriptionNodes);

    // Subtract the attribute count for error validation
    setCheckedAttributeCount(checkedAttributeCount - numChecked);
  };

  const onChangeExposableCdmCheckbox = (
    checked: boolean, 
    filteredTenantIndex: number, 
    filteredCdmIndex: number
  ): void => {

    // Set isAllSelected and isCheckedAttribute for filteredSubscriptionNodes cdm
    const newFilteredSubscriptionNodes: SubscriptionNode[] = JSON.parse(
      JSON.stringify(filteredSubscriptionNodes) // deep copy
    );
    const newFilteredCdm = newFilteredSubscriptionNodes[filteredTenantIndex].children[filteredCdmIndex];
    newFilteredCdm.isAllSelected = checked;
    newFilteredCdm.children.forEach((newFilteredAttribute) => {
        newFilteredAttribute.isCheckedAttribute = checked;
    });
    setFilteredSubscriptionNodes(newFilteredSubscriptionNodes);

    // Set isAllSelected and isCheckedAttribute for formattedSubscriptionNodes cdm
    const newFormattedSubscriptionNodes: SubscriptionNode[] = JSON.parse(
      JSON.stringify(formattedSubscriptionNodes) // deep copy
    );
    const { formattedTenantIndex, formattedCdmIndex } = getFormattedIndexesFromCdmNode(newFilteredCdm.pathFromRoot);
    const newFormattedCdm = newFormattedSubscriptionNodes[formattedTenantIndex].children[formattedCdmIndex]
    newFormattedCdm.isAllSelected = checked;
    newFormattedCdm.children.forEach((newFormattedAttribute) => {
      newFormattedAttribute.isCheckedAttribute = checked;
    });
    setFormattedSubscriptionNodes(newFormattedSubscriptionNodes);

    // Adjust the attribute count for error validation
    const numFormattedAttributes = newFormattedCdm.children.length;
    setCheckedAttributeCount(checked ? 
      checkedAttributeCount + numFormattedAttributes : 
      checkedAttributeCount - numFormattedAttributes);
  };

  const renderExposableCdm = (
    cdmNode: SubscriptionNode, 
    filteredTenantIndex: number, 
    filteredCdmIndex: number): JSX.Element => {
    return (
      <Styled.AttributeSubscriptionsContainer>

        <Checkbox
          key={cdmNode.pathFromRoot + "###SelectAttributesBelow"}
          label={"Select Attributes Below"}
          id={cdmNode.pathFromRoot + "###SelectAttributesBelow"}
          ariaLabel={`${cdmNode.name} Select Attributes Below`}
          checked={cdmNode.isAllSelected}
          onChange={(ev, checked): void =>
            onChangeExposableCdmCheckbox(checked, filteredTenantIndex, filteredCdmIndex)
          }
          styles={checkBoxStyles}
        />

        {filteredSubscriptionNodes[filteredTenantIndex]
          .children[filteredCdmIndex]
          .children
          .map((attributeNode) =>
            <Checkbox
              key={attributeNode.pathFromRoot}
              label={attributeNode.name}
              id={attributeNode.pathFromRoot}
              ariaLabel={`${attributeNode.name} Check Box`}
              checked={attributeNode.isCheckedAttribute}
              styles={checkBoxStyles}
              disabled={true}
            />
          )
        }

      </Styled.AttributeSubscriptionsContainer>
    );
  };

  const isAllApproved = (cdmNode: SubscriptionNode): boolean => {
    return cdmNode.children.length > 0 && cdmNode.children.every((attr) => attr.isApprovedAttribute);
  };

  const renderCdmHeader = (
      cdmNode: SubscriptionNode, 
      filteredTenantIndex: number, 
      filteredCdmIndex: number): JSX.Element => {
    return (
      <Styled.CdmHeaderContainer>

        <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
          <Styled.CdmText>{cdmNode.name}</Styled.CdmText>
          {isAllApproved(cdmNode) && <Styled.AllApprovedBadge>All Approved</Styled.AllApprovedBadge>}
          {cdmNode.isEO14117Protected && (
            <Stack horizontal tokens={{ childrenGap: 4 }} verticalAlign="center">
              <TooltipHost content="This CDM is protected under Executive Order 14117">
                <Icon
                  aria-label="EO14117 Protected"
                  iconName="Lock"
                  className={lockIconClass}
                />
              </TooltipHost>
              <Styled.EOProtectedTextStyled>(EO14117 Protected)</Styled.EOProtectedTextStyled>
            </Stack>
          )}
        </Stack>

        {searchQuery.length === 0 && !cdmNode.isAllSelected && (
          <Styled.SelectAllText
            aria-label="Select All Text"
            onClick={() => onChangeSelectAll(filteredTenantIndex, filteredCdmIndex)}
          >
            Select All
          </Styled.SelectAllText>
        )}

        {searchQuery.length === 0 && cdmNode.isAllSelected && (
          <Styled.SelectAllText 
            aria-label="Deselect All Text"
            onClick={() => onChangeDeselectAll(filteredTenantIndex, filteredCdmIndex)}
          >
            Deselect All
          </Styled.SelectAllText>
        )}
      </Styled.CdmHeaderContainer>
    );
  };

  return (
    <Styled.CdmSubscriptionsContainer>
      {filteredSubscriptionNodes[filteredTenantIndex].children.map((cdmNode, filteredCdmIndex) => (
        <div key={cdmNode.pathFromRoot}>

          {renderCdmHeader(cdmNode, filteredTenantIndex, filteredCdmIndex)}

          {cdmNode.type === SubscriptionNodeTypes.Cdm && (
            <AttributeSubscriptions 
              formattedSubscriptionNodes={formattedSubscriptionNodes}
              setFormattedSubscriptionNodes={setFormattedSubscriptionNodes}
              filteredSubscriptionNodes={filteredSubscriptionNodes}
              setFilteredSubscriptionNodes={setFilteredSubscriptionNodes}
              isAttributeSubscriptionLoading={isAttributeSubscriptionLoading}
              searchQuery={searchQuery}
              checkedAttributeCount={checkedAttributeCount}
              setCheckedAttributeCount={setCheckedAttributeCount}
              filteredTenantIndex={filteredTenantIndex}
              filteredCdmIndex={filteredCdmIndex}
              egressType={egressType}
            />
          )}

          {cdmNode.type === SubscriptionNodeTypes.ExposableCdm && (
            <>
              {renderExposableCdm(cdmNode, filteredTenantIndex, filteredCdmIndex)}
            </>
          )}
        </div>
      ))}
    </Styled.CdmSubscriptionsContainer>
  );
}
  